<template>
  <div class="c-app preika-app">
    <template v-if="accessReady">
      <TheAdminSidebar :access-context="accessContext"/>

      <CWrapper>
        <TheHeader/>
        <div class="c-body">
          <main class="c-main">
            <CContainer fluid>
              <!--
                2要素が近く必須になることの案内。

                **出さなければ、案内の段そのものが無いのと同じ。**
                back は印を返していたのに、読む側がどこにも無かった。
                「案内→促し→必須」の案内が、中の enum として在るだけ
                だった。施行日の当日にいきなり止まるのを避けるために
                段を作ったのに、その1段目が誰にも見えていなかった。

                閉じられるようにする。毎日出て消せないと、そのうち
                読まれなくなる。閉じてもその日のうちは覚えているだけで、
                入り直せばまた出る（**まだ登録していないなら、必要な
                案内であることは変わらない**）。
              -->
              <div
                v-if="upcomingMfaNotice"
                class="alert alert-warning preika-mfa-notice"
                role="status"
              >
                <span>{{ upcomingMfaNotice }}</span>
                <button
                  type="button"
                  class="close"
                  aria-label="閉じる"
                  @click="dismissUpcomingMfaNotice"
                >&times;</button>
              </div>
              <transition name="preika-page" mode="out-in">
                <router-view
                  :key="routeViewKey"
                  :class="routeViewClasses"
                  :data-route-kind="routeViewKind"
                />
              </transition>
            </CContainer>
          </main>
          <TheFooter/>
        </div>
      </CWrapper>

      <GlobalCommandPalette
        :authority="displayAuthority"
        :access-context="accessContext"
      />
      <div class="preika-guide-launchers" aria-label="ヘルプ・機能ガイド">
        <button
          type="button"
          class="preika-help-launcher"
          title="現在の画面の操作と全機能ガイドを開く"
          aria-label="現在の画面の操作と全機能ガイドを開く"
          aria-haspopup="dialog"
          :aria-expanded="String(helpVisible)"
          @click="helpVisible = true"
        >
          <CIcon name="cil-lightbulb"/>
          <span>ヘルプ</span>
        </button>
      </div>
      <!--
        **使えない画面の手引きは出さない。**権限だけで絞ると、契約
        していない機能や、まだ出していない機能の手引きが残る。
        メニュー・直接URL・検索窓と同じ1本（canAccessPath）で決める。
      -->
      <HelpCenter
        :visible="helpVisible"
        :authority="displayAuthority"
        :access-context="accessContext"
        @close="helpVisible = false"
      />
    </template>

    <main
      v-else
      class="preika-authority-gate"
      :aria-busy="accessLoading ? 'true' : 'false'"
    >
      <section
        v-if="accessLoadError"
        class="preika-authority-card preika-authority-card--error"
        role="alert"
        aria-live="assertive"
      >
        <span class="preika-authority-card__icon" aria-hidden="true">
          <CIcon name="cil-warning"/>
        </span>
        <p class="preika-authority-card__eyebrow">ACCESS CHECK</p>
        <h1>権限や設定情報を確認できませんでした</h1>
        <p>{{ accessLoadError }}</p>
        <div class="preika-authority-card__actions">
          <button
            type="button"
            class="btn btn-primary"
            :disabled="accessLoading"
            @click="loadAccessContext(true)"
          >
            {{ accessLoading ? '確認しています' : 'もう一度確認する' }}
          </button>
          <button
            type="button"
            class="btn btn-outline-secondary"
            @click="redirectToLogin"
          >
            ログイン画面へ
          </button>
        </div>
      </section>

      <section
        v-else
        class="preika-authority-card preika-authority-card--loading"
        role="status"
        aria-live="polite"
      >
        <span class="my-spinner preika-authority-spinner" aria-hidden="true"></span>
        <p class="preika-authority-card__eyebrow">ACCESS CHECK</p>
        <h1>利用できる画面を確認しています</h1>
        <p>契約プラン、権限グループ、権限の確認後、利用できるメニューだけを表示します。</p>
      </section>
    </main>
  </div>
</template>

<script>
import { japaneseOr } from '@/utils/japaneseText'
import TheAdminSidebar from './TheAdminSidebar'
import TheHeader from './TheHeader'
import TheFooter from './TheFooter'
import GlobalCommandPalette from '../common/GlobalCommandPalette'
import HelpCenter from '../common/HelpCenter'
import {
  clearAccessContext,
  getAccessContext
} from '../services/accessContext'
import { presentationAuthority } from '../utils/accessPolicy'
import {
  resolveRoutePresentation,
  routePresentationClasses
} from '../utils/routePresentation'
import { safeErrorDigest } from '../utils/safeResponseMessage'
import { PRODUCT_NAME } from '../common/productName'
import { SECURITY_SCREEN_NAME } from '../common/screenNames'
import { fetchAdminRequirementNotice } from '../services/mfa'

export default {
  name: 'TheContainer',

  components: {
    TheHeader,
    TheFooter,
    TheAdminSidebar,
    GlobalCommandPalette,
    HelpCenter
  },

  data () {
    return {
      accessContext: null,
      accessLoading: true,
      accessLoadError: '',
      helpVisible: false,
      /*
       * 2要素が近く必須になるか。**サーバへ聞く。**
       *
       * ログインの応答を控える形にすると、その応答を受け取れる人にしか
       * 案内が出ない。Google / Microsoft から入った人は受け取れない
       * （画面へ飛ばして戻す形なので、応答の中身が途中で捨てられる）。
       */
      upcomingMfaRequired: false,
      upcomingMfaFrom: '',
      upcomingMfaDismissed: false
    }
  },

  computed: {
    /**
     * 2要素が近く必須になることの案内。出すものが無ければ空。
     *
     * <p><b>日は作らない。</b>決まっていなければ「近いうちに」とだけ
     * 言う。出まかせの日を見せると、その日までに登録すればよいと
     * 読まれる。</p>
     */
    upcomingMfaNotice () {
      if (this.upcomingMfaDismissed) return ''
      if (!this.upcomingMfaRequired) return ''

      const from = this.upcomingMfaFrom
      /* **行き先はメニューと同じ名前で言う。**別々に持つと、名前を
         変えたときに案内だけ古いまま残り、探しに行った人が見つけられない。 */
      const where = '「' + SECURITY_SCREEN_NAME + '」'
      return from
        ? ('管理者の2要素認証が ' + from
           + ' から必須になります。' + where
           + 'から先に登録してください。')
        : ('管理者の2要素認証が近いうちに必須になります。'
           + where + 'から先に登録してください。')
    },

    accessReady () {
      return Boolean(this.accessContext)
    },

    displayAuthority () {
      return presentationAuthority(this.accessContext)
    },

    routeViewKey () {
      return this.$route.fullPath
    },

    routeViewKind () {
      return resolveRoutePresentation(this.$route)
    },

    routeViewClasses () {
      return routePresentationClasses(this.$route)
    }
  },

  created () {
    this.loadAccessContext(false)
    /*
     * 案内はサーバへ聞く。**入り口によらずここ1本だけを通る。**
     * 業務画面の表示は待たせない（案内のために画面が止まらない）。
     */
    this.loadUpcomingMfaNotice()
  },

  methods: {
    /**
     * 案内をサーバへ聞く。
     *
     * <p>入り口によらず、ここ1本だけを通る。合言葉・パスキー・Google・
     * Microsoft・再読込・業務画面への直リンクが全部同じになる。</p>
     *
     * <p>読めなかったときは<b>出さない</b>。案内が1回出ないだけで、
     * 施行日そのものは動かない。ここで止めると、案内のために業務画面が
     * 開かなくなる。</p>
     */
    async loadUpcomingMfaNotice () {
      try {
        const notice = await fetchAdminRequirementNotice()
        this.upcomingMfaRequired = Boolean(notice && notice.upcoming)
        this.upcomingMfaFrom = (notice && notice.requiredFrom) || ''
      } catch (error) {
        this.upcomingMfaRequired = false
        this.upcomingMfaFrom = ''
      }
    },

    /**
     * 案内を閉じる。
     *
     * <p>毎日出て消せないと、そのうち読まれなくなる。ただし
     * <b>閉じても覚えておくのはこの画面のあいだだけ</b>。入り直せば
     * また出る。まだ登録していないなら、必要な案内であることは
     * 変わらないため。</p>
     */
    dismissUpcomingMfaNotice () {
      this.upcomingMfaDismissed = true
    },

    loadAccessContext (force) {
      this.accessContext = null
      this.helpVisible = false
      this.accessLoadError = ''
      this.accessLoading = true

      return getAccessContext({ force: Boolean(force) })
        .then(context => {
          this.accessContext = context
        })
        .catch(error => {
          console.error('access-contextの取得に失敗しました:', safeErrorDigest(error))
          const status = error && error.response ? Number(error.response.status) : 0

          if (status === 401) {
            this.redirectToLogin()
            return
          }

          if (status === 403) {
            this.accessLoadError = PRODUCT_NAME + 'を利用できるアカウントではありません。社内の管理者へご確認ください。'
            return
          }

          this.accessLoadError = japaneseOr(error && error.message,
            status >= 500
              ? 'ご利用の権限を確認できませんでした。時間をおいて再度お試しください。'
              : '通信状況を確認して、もう一度お試しください。')
        })
        .finally(() => {
          this.accessLoading = false
        })
    },

    redirectToLogin () {
      clearAccessContext()
      try {
        window.sessionStorage.removeItem('level')
      } catch (error) {
        console.warn('ローカルの互換権限情報を削除できませんでした:', error)
      }

      if (this.$route && this.$route.name === 'Login') return
      this.$router.replace({ name: 'Login' }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.preika-authority-gate {
  display: flex;
  min-height: 100vh;
  align-items: center;
  justify-content: center;
  padding: 28px;
  background:
    radial-gradient(circle at 18% 12%, rgba(58, 160, 221, .14), transparent 34%),
    linear-gradient(145deg, #f4f9fc, #edf3f7);
}

.preika-authority-card {
  width: min(100%, 560px);
  padding: 38px;
  border: 1px solid rgba(137, 163, 178, .26);
  border-radius: 22px;
  background: rgba(255, 255, 255, .96);
  box-shadow: 0 22px 56px rgba(22, 57, 76, .13);
  color: #193b4e;
  text-align: center;
}

.preika-authority-card__icon {
  display: inline-flex;
  width: 52px;
  height: 52px;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
  border-radius: 17px;
  background: rgba(225, 74, 74, .1);
  color: #b62d3b;
}

.preika-authority-card__icon .c-icon {
  width: 24px;
  height: 24px;
}

.preika-authority-card__eyebrow {
  margin: 0 0 8px;
  color: #0a6ca8;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: .15em;
}

.preika-authority-card h1 {
  margin: 0;
  color: #123449;
  font-size: 24px;
  line-height: 1.45;
}

.preika-authority-card > p:not(.preika-authority-card__eyebrow) {
  margin: 14px 0 0;
  color: #607884;
  font-size: 12px;
  line-height: 1.8;
}

.preika-authority-card__actions {
  display: flex;
  justify-content: center;
  margin-top: 24px;
  gap: 10px;
}

.preika-authority-spinner {
  display: inline-block;
  margin-bottom: 18px;
}

.preika-page-enter-active,
.preika-page-leave-active {
  transition: opacity .18s ease, transform .18s ease;
}

.preika-page-enter,
.preika-page-leave-to {
  opacity: 0;
  transform: translateY(5px);
}

.preika-guide-launchers {
  position: fixed;
  z-index: 1040;
  right: 24px;
  bottom: 24px;
  display: flex;
  align-items: center;
}

.preika-help-launcher {
  display: inline-flex;
  min-height: 48px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 16px;
  border: 1px solid rgba(255, 255, 255, .72);
  border-radius: 16px;
  background: linear-gradient(145deg, #0a66b2, #084b83);
  box-shadow: 0 16px 34px rgba(5, 53, 89, .26);
  color: #fff;
  cursor: pointer;
  font-size: 12px;
  font-weight: 800;
  transition: box-shadow .16s ease, transform .16s ease;
}

.preika-help-launcher:hover,
.preika-help-launcher:focus {
  outline: none;
  box-shadow: 0 20px 40px rgba(5, 53, 89, .34), 0 0 0 4px rgba(44, 145, 220, .18);
  color: #fff;
  transform: translateY(-2px);
}

.preika-help-launcher .c-icon {
  width: 18px;
  height: 18px;
}

@media (max-width: 767.98px) {
  .preika-authority-gate {
    padding: 16px;
  }

  .preika-authority-card {
    padding: 28px 20px;
    border-radius: 18px;
  }

  .preika-authority-card h1 {
    font-size: 20px;
  }

  .preika-authority-card__actions {
    flex-direction: column;
  }

  .preika-guide-launchers {
    right: 14px;
    bottom: 14px;
  }

  .preika-help-launcher {
    width: 48px;
    height: 48px;
    padding: 0;
    border-radius: 15px;
  }

  .preika-help-launcher span {
    display: none;
  }
}
</style>
